/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/use-scope-sheet-paste.ts
 * PURPOSE: Encapsulates paste-mapping modal state and all paste logic for ScopeSheetGrid.
 */

import { useState, useCallback, useMemo } from 'react'
import type { QuoteSection, QuoteLineItem } from '@/lib/quoteApi'
import type { Column } from './ScopeSheetGridTypes'
import type { ColumnMapping } from '../PasteMappingModal'
import usePasteMappingPresets from '@/hooks/usePasteMappingPresets'
import { isNumericColumn } from './scope-sheet-columns'

interface FlatItem {
  sectionId: string
  itemId: string
  item: QuoteLineItem
  rowIndex: number
}

interface UseScopeSheetPasteParams {
  sections: QuoteSection[]
  flatLineItems: FlatItem[]
  allColumns: Column[]
  onUpdateItem: (sectionId: string, itemId: string, patch: Partial<QuoteLineItem>) => void
  onPasteWithCreate?: (
    sectionId: string,
    existingItems: QuoteLineItem[],
    pasteData: Array<Record<string, unknown>>,
    startRow: number
  ) => Promise<void>
  handleCellChange: (rowIndex: number, colIndex: number, value: string) => void
}

const PASTE_MODAL_THRESHOLD_ROWS = 3
const PASTE_MODAL_THRESHOLD_COLS = 5

/** Heuristic: if 60%+ of first row cells are non-numeric, it's probably a header row. */
function looksLikeExcelWithHeaders(data: string[][]): boolean {
  if (data.length < 2 || data[0].length < 2) return false
  let headerLikeCount = 0
  for (const cell of data[0]) {
    const trimmed = cell.trim()
    if (trimmed && isNaN(Number(trimmed))) headerLikeCount++
  }
  return headerLikeCount >= data[0].length * 0.6
}

export default function useScopeSheetPaste({
  sections,
  flatLineItems,
  allColumns,
  onUpdateItem,
  onPasteWithCreate,
  handleCellChange,
}: UseScopeSheetPasteParams) {
  const [pasteModalOpen, setPasteModalOpen] = useState(false)
  const [pendingPasteData, setPendingPasteData] = useState<string[][] | null>(null)
  const [pendingPasteTarget, setPendingPasteTarget] = useState<{
    sectionId: string
    startRow: number
    startCol: number
  } | null>(null)

  const { presets, savePreset, findMatchingPreset, incrementUseCount } = usePasteMappingPresets()

  const applyPasteWithMapping = useCallback(
    async (data: string[][], mappings: ColumnMapping[], sectionId: string, startRow: number) => {
      const hasHeaders = looksLikeExcelWithHeaders(data)
      const dataRows = hasHeaders ? data.slice(1) : data
      if (dataRows.length === 0) return

      const section = sections.find((s) => s.id === sectionId)
      if (!section) return

      const pasteData = dataRows.map((rowData) => {
        const record: Record<string, unknown> = {}
        for (const mapping of mappings) {
          const cellValue = rowData[mapping.sourceIndex] ?? ''
          const column = allColumns.find((c) => c.id === mapping.targetField)
          if (column) {
            record[mapping.targetField] = isNumericColumn(mapping.targetField)
              ? cellValue ? parseFloat(cellValue) : null
              : cellValue
          }
        }
        return record
      })

      const neededRows = startRow + pasteData.length
      const existingRows = section.items?.length || 0

      if (neededRows > existingRows && onPasteWithCreate) {
        await onPasteWithCreate(sectionId, section.items || [], pasteData, startRow)
      } else {
        pasteData.forEach((record, rowOffset) => {
          const flatItem = flatLineItems[startRow + rowOffset]
          if (!flatItem) return
          onUpdateItem(flatItem.sectionId, flatItem.itemId, record)
        })
      }
    },
    [sections, flatLineItems, onUpdateItem, onPasteWithCreate, allColumns],
  )

  const handlePasteMappingApply = useCallback(
    (mappings: ColumnMapping[]) => {
      if (!pendingPasteData || !pendingPasteTarget) return
      applyPasteWithMapping(pendingPasteData, mappings, pendingPasteTarget.sectionId, pendingPasteTarget.startRow)
      setPendingPasteData(null)
      setPendingPasteTarget(null)
      setPasteModalOpen(false)
    },
    [pendingPasteData, pendingPasteTarget, applyPasteWithMapping],
  )

  const handleSavePasteMapping = useCallback(
    (mappings: ColumnMapping[]) => {
      const fieldNames = mappings.map((m) => m.targetField).slice(0, 3).join(', ')
      savePreset(`Mapping: ${fieldNames}...`, mappings)
    },
    [savePreset],
  )

  const availablePasteFields = useMemo(
    () =>
      allColumns
        .filter((col) => col.editable)
        .map((col) => ({
          id: col.id,
          label: col.label,
          type: isNumericColumn(col.id) ? ('number' as const) : ('text' as const),
          required: col.id === 'description',
        })),
    [allColumns],
  )

  const savedMappingForModal = useMemo(() => {
    if (!pendingPasteData || pendingPasteData.length === 0) return null
    const matched = findMatchingPreset(pendingPasteData[0] || [])
    const preset = matched || presets[0] || null
    if (!preset) return null
    return { mappings: preset.mappings, autoDetected: false, savedAt: preset.updatedAt }
  }, [pendingPasteData, findMatchingPreset, presets])

  const handlePasteData = useCallback(
    async (startRow: number, startCol: number, data: string[][]) => {
      const shouldShowModal =
        data.length >= PASTE_MODAL_THRESHOLD_ROWS ||
        (data[0]?.length ?? 0) >= PASTE_MODAL_THRESHOLD_COLS ||
        looksLikeExcelWithHeaders(data)

      let sectionId: string | null = null
      if (flatLineItems.length > 0) {
        sectionId = flatLineItems[Math.min(startRow, flatLineItems.length - 1)]?.sectionId || null
      }
      if (!sectionId && sections.length > 0) sectionId = sections[0].id
      if (!sectionId) return

      if (shouldShowModal) {
        const matchedPreset = findMatchingPreset(data[0] || [])
        if (matchedPreset) {
          incrementUseCount(matchedPreset.id)
          await applyPasteWithMapping(data, matchedPreset.mappings, sectionId, startRow)
          return
        }
        setPendingPasteData(data)
        setPendingPasteTarget({ sectionId, startRow, startCol })
        setPasteModalOpen(true)
        return
      }

      // Small paste — apply directly without modal
      const neededRows = startRow + data.length
      if (neededRows > flatLineItems.length && onPasteWithCreate) {
        const section = sections.find((s) => s.id === sectionId)
        if (section) {
          const pasteData = data.map((rowData) => {
            const record: Record<string, unknown> = {}
            rowData.forEach((cellValue, colIdx) => {
              const column = allColumns[startCol + colIdx]
              if (column && column.editable && !column.hidden) record[column.id] = cellValue
            })
            return record
          })
          await onPasteWithCreate(sectionId, section.items || [], pasteData, startRow)
          return
        }
      }

      data.forEach((rowData, rowOffset) => {
        const targetRow = startRow + rowOffset
        if (targetRow >= flatLineItems.length) return
        rowData.forEach((cellValue, colOffset) => {
          const targetCol = startCol + colOffset
          if (targetCol >= allColumns.length) return
          handleCellChange(targetRow, targetCol, cellValue)
        })
      })
    },
    [flatLineItems, sections, onPasteWithCreate, allColumns, handleCellChange, findMatchingPreset, incrementUseCount, applyPasteWithMapping],
  )

  const closePasteModal = useCallback(() => {
    setPasteModalOpen(false)
    setPendingPasteData(null)
    setPendingPasteTarget(null)
  }, [])

  return {
    pasteModalOpen,
    pendingPasteData,
    pendingPasteTarget,
    handlePasteData,
    handlePasteMappingApply,
    handleSavePasteMapping,
    availablePasteFields,
    savedMappingForModal,
    closePasteModal,
  }
}
